import { Card, Switch } from "@ttc3k/trekker";
import { Maybe, SurveyQuestion } from "gql/generated";
import { ReactNode } from "react";
import { cva } from "styled-system/css";
import { Box, Flex, FlexProps, styled } from "styled-system/jsx";
import { getMultilingualString } from "shared/utils";
import { SurveyQuestionInput } from "./SurveyQuestionInput";
import { BusinessScopedFieldGroup } from "./BusinessScopedFieldGroup";
import {
  getEnabledBusinessIds,
  isPerBusinessToggle,
  type BusinessAnswer,
  type BusinessScoped,
} from "@shared/businessScope";
import {
  groupSurveyQuestions,
  isToggleOn,
  isToggleQuestionType,
} from "./surveyQuestionVisibility";

const toggleCardSurfaceStyles = cva({
  base: {
    width: "full",
    outline: "0 transparent solid",
    transitionDuration: "normal",
    transitionProperty: "border, outline",
    transitionTimingFunction: "default",
    boxShadow: "none",
    overflow: "visible",
  },
  variants: {
    visual: {
      unChecked: {},
      checked: {
        borderColor: "border.dark",
        outlineWidth: "1px",
        outlineColor: "border.dark",
      },
    },
  },
  defaultVariants: {
    visual: "unChecked",
  },
});

const ToggleCardSurface = styled(Card.Surface, toggleCardSurfaceStyles);

type ToggleQuestionCardProps = {
  title: string;
  description: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  footer?: ReactNode;
};

export const ToggleQuestionCard = ({
  title,
  description,
  checked,
  onCheckedChange,
  footer,
}: ToggleQuestionCardProps) => (
  <ToggleCardSurface visual={checked ? "checked" : "unChecked"}>
    <Card.Body bg="white" py="250" overflow="visible" borderRadius="inherit">
      <Card.BodyContent alignItems="center">
        <Card.Title mainText={title} subText={description} />
        <Switch
          checked={checked}
          onCheckedChange={e => onCheckedChange(e.checked)}
        />
      </Card.BodyContent>
      {footer && <Box pt="100">{footer}</Box>}
    </Card.Body>
  </ToggleCardSurface>
);

type SurveyQuestionListProps = {
  questions: SurveyQuestion[];
  getResponse: (question: SurveyQuestion) => string[] | Maybe<string>[];
  onResponseChange: (questionId: string, response: string[]) => void;
  getError?: (question: SurveyQuestion) => string | undefined;
  organizationId?: string;
  language?: Parameters<typeof getMultilingualString>[1];
  gap?: FlexProps["gap"];
  /**
   * Businesses the answers are for. With these, a per-business toggle is
   * switched on per business and its follow-ups are answered for each one.
   */
  businesses?: BusinessScoped[];
  getBusinessAnswers?: (question: SurveyQuestion) => BusinessAnswer[];
  onBusinessResponseChange?: (
    questionId: string,
    operatorId: string,
    response: string[],
  ) => void;
};

/**
 * Renders survey questions in order. A Toggle question becomes a card with a
 * switch; its follow-up questions render inside the card while it's on.
 */
export const SurveyQuestionList = ({
  questions,
  getResponse,
  onResponseChange,
  getError,
  organizationId,
  language,
  gap = "200",
  businesses,
  getBusinessAnswers,
  onBusinessResponseChange,
}: SurveyQuestionListProps) => {
  const groups = groupSurveyQuestions(questions);
  const scoped =
    !!businesses?.length && !!getBusinessAnswers && !!onBusinessResponseChange;

  const renderInput = (question: SurveyQuestion) => (
    <SurveyQuestionInput
      key={question._id}
      question={question}
      response={getResponse(question)}
      onResponseChange={onResponseChange}
      organizationId={organizationId}
      error={getError?.(question)}
      description={
        question.description
          ? getMultilingualString(question.description, language) || undefined
          : undefined
      }
    />
  );

  return (
    <Flex flexDirection="column" gap={gap}>
      {groups.map(({ question, children }) => {
        if (!isToggleQuestionType(question.type)) return renderInput(question);
        const checked = isToggleOn(getResponse(question));
        if (scoped && isPerBusinessToggle(question)) {
          const enabledIds = getEnabledBusinessIds({
            byBusiness: getBusinessAnswers(question),
          });
          const answerFor = (child: SurveyQuestion, operatorId: string) =>
            getBusinessAnswers(child).find(b => b.operatorId === operatorId)
              ?.response ?? [];
          return (
            <ToggleQuestionCard
              key={question._id}
              title={getMultilingualString(question.name, language)}
              description={
                question.description
                  ? getMultilingualString(question.description, language)
                  : ""
              }
              checked={checked}
              onCheckedChange={v =>
                businesses.forEach(b =>
                  onBusinessResponseChange(question._id, b.operatorId, [String(v)]),
                )
              }
              footer={
                checked ? (
                  <BusinessScopedFieldGroup
                    businesses={businesses}
                    checkedOperatorIds={enabledIds}
                    onCheckedChange={(operatorId, on) =>
                      onBusinessResponseChange(question._id, operatorId, [String(on)])
                    }
                    gap={gap}
                    renderFields={
                      children.length > 0
                        ? operatorId =>
                            children.map(child => (
                              <SurveyQuestionInput
                                key={`${child._id}-${operatorId}`}
                                idScope={`${child._id}-${operatorId}`}
                                question={child}
                                response={answerFor(child, operatorId)}
                                onResponseChange={(questionId, response) =>
                                  onBusinessResponseChange(questionId, operatorId, response)
                                }
                                organizationId={organizationId}
                                description={
                                  child.description
                                    ? getMultilingualString(child.description, language) ||
                                      undefined
                                    : undefined
                                }
                              />
                            ))
                        : undefined
                    }
                  />
                ) : undefined
              }
            />
          );
        }
        return (
          <ToggleQuestionCard
            key={question._id}
            title={getMultilingualString(question.name, language)}
            description={
              question.description
                ? getMultilingualString(question.description, language)
                : ""
            }
            checked={checked}
            onCheckedChange={v => onResponseChange(question._id, [String(v)])}
            footer={
              checked && children.length > 0 ? (
                <Flex flexDirection="column" gap={gap}>
                  {children.map(renderInput)}
                </Flex>
              ) : undefined
            }
          />
        );
      })}
    </Flex>
  );
};
